import { useState } from "react";
import { Edit, Eye, UserCheck, Archive, Share, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useProfileSwitch } from "@/contexts/ProfileSwitchContext";
import { useAdditionalProfiles } from "@/hooks/useAdditionalProfiles";
import ActionDrawer, { ActionButton } from "@/components/ui/action-drawer";
import { EditManagedProfileModal } from "./EditManagedProfileModal";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

interface ProfileManageDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  profile: any;
  onHandoverToken: (profileId: string) => void;
}

export const ProfileManageDrawer = ({ 
  isOpen, 
  onClose, 
  profile, 
  onHandoverToken 
}: ProfileManageDrawerProps) => {
  const { toast } = useToast();
  const { switchToProfile, activeProfile } = useProfileSwitch();
  const { deleteProfile, isDeleting } = useAdditionalProfiles();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const handleEditProfile = () => {
    setIsEditModalOpen(true);
  };

  const handleViewTimeline = () => {
    // Navigate to timeline
    window.location.href = `/timeline/${profile.username}`;
  };

  const handleSwitchActiveProfile = () => {
    switchToProfile(profile.id);
    toast({
      title: "Profile Switched",
      description: `Now managing ${profile.first_name || profile.username}'s timeline`,
    });
  };

  const handleArchiveProfile = () => {
    toast({
      title: "Archive Profile",
      description: "Archive functionality will be available soon",
    });
  };

  const handleDeleteProfile = () => {
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteProfile = async () => {
    try {
      await deleteProfile(profile.id);
      setIsDeleteDialogOpen(false);
      onClose();
    } catch (error) {
      // Error is handled by the hook
    }
  };

  const displayName = profile.first_name && profile.last_name
    ? `${profile.first_name} ${profile.last_name}`
    : profile.username;

  const isActiveProfile = activeProfile?.id === profile.id;

  const actions: ActionButton[] = [
    {
      text: "Edit Profile",
      icon: Edit,
      onClick: handleEditProfile,
      variant: "outline",
      closeAfterAction: false
    },
    {
      text: "View Timeline",
      icon: Eye,
      onClick: handleViewTimeline,
      variant: "outline"
    },
    {
      text: isActiveProfile ? "Active Profile" : "Switch to Profile",
      icon: UserCheck,
      onClick: handleSwitchActiveProfile,
      variant: isActiveProfile ? "secondary" : "outline",
      disabled: isActiveProfile
    },
    {
      text: "Transfer Ownership",
      icon: Share,
      onClick: () => onHandoverToken(profile.id),
      variant: "outline"
    },
    {
      text: "Archive Profile",
      icon: Archive,
      onClick: handleArchiveProfile,
      variant: "outline"
    },
    {
      text: "Delete Profile",
      icon: Trash2,
      onClick: handleDeleteProfile,
      variant: "destructive",
      closeAfterAction: false
    }
  ];

  return (
    <>
      <ActionDrawer
        title={`Manage ${displayName}`}
        description="Choose an action for this profile"
        actions={actions}
        isOpen={isOpen}
        onClose={onClose}
      />

      <EditManagedProfileModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        profile={profile}
      />

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Profile</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {displayName}'s profile? This action cannot be undone and will permanently delete all timeline entries and data associated with this profile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteProfile}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? "Deleting..." : "Delete Profile"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};